import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, User, MessageSquare, Truck, Zap, Tag, MapPin } from 'lucide-react';
import { Opportunity, OPPORTUNITY_STATUSES } from '@/hooks/useOpportunities';

interface OpportunityCardProps {
  opportunity: Opportunity;
  onClick: () => void;
  onTagClick?: (e: React.MouseEvent) => void;
}

export function OpportunityCard({ opportunity, onClick, onTagClick }: OpportunityCardProps) {
  const status = OPPORTUNITY_STATUSES.find(s => s.id === opportunity.status);
  
  const getOrgIcon = () => {
    if (opportunity.organization?.logo_url || opportunity.organization?.favicon_url) {
      return (
        <img
          src={opportunity.organization.logo_url || opportunity.organization.favicon_url || ''}
          alt=""
          className="h-4 w-4 object-contain flex-shrink-0"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    if (opportunity.organization?.website) {
      const domain = opportunity.organization.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      return (
        <img
          src={`https://www.google.com/s2/favicons?sz=32&domain=${domain}`}
          alt=""
          className="h-4 w-4 object-contain flex-shrink-0"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    return <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
  };

  // Get all contacts (from opportunity_contacts or legacy contact_id)
  const contacts = opportunity.opportunity_contacts?.length 
    ? opportunity.opportunity_contacts.map(oc => oc.contact)
    : opportunity.contact ? [opportunity.contact] : [];

  const firstContactName = contacts[0] 
    ? `${contacts[0].first_name || ''} ${contacts[0].last_name || ''}`.trim()
    : null;

  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-shadow border-l-4"
      style={{ borderLeftColor: status?.color }}
      onClick={onClick}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="font-medium text-foreground break-words flex-1 min-w-0 leading-tight line-clamp-3">{opportunity.name}</div>
          {onTagClick && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onTagClick(e);
              }}
            >
              <Tag className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        
        {opportunity.organization && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {getOrgIcon()}
            <span className="break-words line-clamp-2">{opportunity.organization.name}</span>
          </div>
        )}
        
        {firstContactName && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {/* Show org favicon for contact if available */}
            {contacts[0]?.org_id && opportunity.organization ? (
              getOrgIcon()
            ) : (
              <User className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="truncate">
              {firstContactName}
              {contacts.length > 1 && ` +${contacts.length - 1}`}
            </span>
          </div>
        )}

        {/* Linked Locations */}
        {opportunity.opportunity_locations && opportunity.opportunity_locations.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
            <span className="truncate">
              {opportunity.opportunity_locations.length === 1 
                ? opportunity.opportunity_locations[0].location?.name || 'Locatie'
                : `${opportunity.opportunity_locations.length} locaties`}
            </span>
          </div>
        )}

        {/* Tags */}
        {opportunity.tags && opportunity.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {opportunity.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {opportunity.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{opportunity.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-between gap-2">
          {/* Fleet info */}
          {(opportunity.total_fleet || opportunity.electric_trucks) && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              {opportunity.total_fleet && (
                <span className="flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5" />
                  {opportunity.total_fleet}
                </span>
              )}
              {opportunity.electric_trucks && (
                <span className="flex items-center gap-1 text-green-600">
                  <Zap className="h-3.5 w-3.5" />
                  {opportunity.electric_trucks}
                </span>
              )}
            </div>
          )}
          
          {(opportunity.notes_count ?? 0) > 0 && (
            <Badge variant="secondary" className="flex items-center gap-1 ml-auto">
              <MessageSquare className="h-3 w-3" />
              {opportunity.notes_count}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
